export const isAllWhitespace = (text: string): boolean => {
  const reg = /^[\s]+$/;
  return !text || text === "" || reg.test(text);
};

export const PASSWORD_CHARSET_ERROR = "Password can only contain letters, numbers, and standard symbols";

// Login rejects any non-ASCII password before verifying it, and control characters cannot be
// typed into a login form, so a password that slips past set-time validation with either locks
// the account out. Mirrors the backend PasswordPolicy exactly.
export const containsUnsupportedPasswordCharacters = (text: string | undefined): boolean => {
  if (!text) {
    return false;
  }
  for (const character of text) {
    const codePoint = character.codePointAt(0) ?? 0;
    if (codePoint < 32 || codePoint > 126) {
      return true;
    }
  }
  return false;
};

export const getPasswordError = (password: string | undefined): string | undefined => {
  if (!password || isAllWhitespace(password)) {
    return "Required";
  }
  if (containsUnsupportedPasswordCharacters(password)) {
    return PASSWORD_CHARSET_ERROR;
  }
  if (
    !window.location.href.includes("localhost") &&
    !window.location.href.includes("10.0.2.2") &&
    password.trim().length < 8
  ) {
    return "Must be at least 8 characters long";
  }
  return undefined;
};

/**
 * Validates a US phone number format
 * @param phone - The phone number string to validate
 * @returns Empty string if valid, error message if invalid
 */
export const validatePhoneNumber = (phone: string): string => {
  if (!phone.trim()) return "Phone number is required";
  const phoneRegex = /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/;
  if (!phoneRegex.test(phone)) {
    return "Please enter a valid phone number (e.g., 123-456-7890)";
  }
  return "";
};

/**
 * Shared phone number validation logic
 * @param value - The phone number string to validate
 * @param isRequired - Whether the field is required
 * @returns Validation result (true if valid, error message if invalid)
 */
const validatePhoneNumberFormat = (value: string, isRequired: boolean = true): string | boolean => {
  const phoneRegex = /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/;
  const errorMessage = "Please enter a valid phone number (e.g., 123-456-7890)";

  if (!value || value.trim() === "") {
    return isRequired ? "Phone number is required" : true;
  }

  return phoneRegex.test(value) || errorMessage;
};

/**
 * Creates phone validation rules for React Hook Form
 * @param isRequired - Whether the field is required (default: true)
 * @returns React Hook Form validation rules object
 */
const createPhoneValidationRules = (isRequired: boolean = true) => {
  const baseRules = {
    pattern: {
      value: /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/,
      message: "Please enter a valid phone number (e.g., 123-456-7890)"
    }
  };

  if (isRequired) {
    return {
      ...baseRules,
      required: "Phone number is required"
    };
  } else {
    return {
      ...baseRules,
      validate: (value: string) => validatePhoneNumberFormat(value, false)
    };
  }
};

/**
 * React Hook Form validation rules for required phone numbers
 */
export const phoneValidationRules = createPhoneValidationRules(true);

/**
 * React Hook Form validation rules for optional phone numbers (like fax)
 * Only validates format when field has content, but doesn't require the field
 */
export const optionalPhoneValidationRules = createPhoneValidationRules(false);

export const STATE_OPTIONS = [
  { label: "Alabama", value: "AL" },
  { label: "Alaska", value: "AK" },
  { label: "Arizona", value: "AZ" },
  { label: "Arkansas", value: "AR" },
  { label: "California", value: "CA" },
  { label: "Colorado", value: "CO" },
  { label: "Connecticut", value: "CT" },
  { label: "Delaware", value: "DE" },
  { label: "District of Columbia", value: "DC" },
  { label: "Florida", value: "FL" },
  { label: "Georgia", value: "GA" },
  { label: "Hawaii", value: "HI" },
  { label: "Idaho", value: "ID" },
  { label: "Illinois", value: "IL" },
  { label: "Indiana", value: "IN" },
  { label: "Iowa", value: "IA" },
  { label: "Kansas", value: "KS" },
  { label: "Kentucky", value: "KY" },
  { label: "Louisiana", value: "LA" },
  { label: "Maine", value: "ME" },
  { label: "Maryland", value: "MD" },
  { label: "Massachusetts", value: "MA" },
  { label: "Michigan", value: "MI" },
  { label: "Minnesota", value: "MN" },
  { label: "Mississippi", value: "MS" },
  { label: "Missouri", value: "MO" },
  { label: "Montana", value: "MT" },
  { label: "Nebraska", value: "NE" },
  { label: "Nevada", value: "NV" },
  { label: "New Hampshire", value: "NH" },
  { label: "New Jersey", value: "NJ" },
  { label: "New Mexico", value: "NM" },
  { label: "New York", value: "NY" },
  { label: "North Carolina", value: "NC" },
  { label: "North Dakota", value: "ND" },
  { label: "Ohio", value: "OH" },
  { label: "Oklahoma", value: "OK" },
  { label: "Oregon", value: "OR" },
  { label: "Pennsylvania", value: "PA" },
  { label: "Rhode Island", value: "RI" },
  { label: "South Carolina", value: "SC" },
  { label: "South Dakota", value: "SD" },
  { label: "Tennessee", value: "TN" },
  { label: "Texas", value: "TX" },
  { label: "Utah", value: "UT" },
  { label: "Vermont", value: "VT" },
  { label: "Virginia", value: "VA" },
  { label: "Washington", value: "WA" },
  { label: "West Virginia", value: "WV" },
  { label: "Wisconsin", value: "WI" },
  { label: "Wyoming", value: "WY" }
];
